import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import type { Brand } from '@/components/finance/BrandSelector';
import { getFriendlyFinanceErrorMessage } from '@/lib/financeErrors';
import { normalizePaymentMethod } from '@/lib/financeConstants';

export interface ReceivablePayment {
  payment_id: string;
  contact_id: string;
  cliente: string;
  deal_id: string;
  description: string;
  amount: number;
  paid_amount: number;
  valor_aberto: number;
  due_date: string;
  dias_atraso: number;
  faixa: string;
  status: string;
  collection_status: string | null;
  vendedor: string;
  business_unit: string;
  brand: string;
  currency: string;
  raw_status?: string;
}

interface UseReceivablesOptions {
  businessUnit?: BusinessUnit;
  brand?: Brand;
  status?: 'all' | 'pending' | 'overdue' | 'paid';
  dueDateFrom?: string;
  dueDateTo?: string;
  limit?: number;
}

export function useReceivables({
  businessUnit = 'all',
  brand = 'all',
  status = 'all',
  dueDateFrom,
  dueDateTo,
  limit = 2000,
}: UseReceivablesOptions = {}) {
  return useQuery({
    queryKey: ['receivables', businessUnit, brand, status, dueDateFrom, dueDateTo, limit],
    queryFn: async () => {
      let query = (supabase.from('v_receivables_detail' as any) as any)
        .select('payment_id,contact_id,cliente,deal_id,description,amount,paid_amount,valor_aberto,due_date,dias_atraso,faixa,status,raw_status,collection_status,vendedor,business_unit,brand,currency')
        .order('due_date', { ascending: true })
        .limit(limit);

      if (businessUnit !== 'all') {
        query = query.eq('business_unit', businessUnit);
      }

      if (brand !== 'all') {
        query = query.eq('brand', brand);
      }

      if (dueDateFrom) {
        query = query.gte('due_date', dueDateFrom);
      }

      if (dueDateTo) {
        query = query.lte('due_date', dueDateTo);
      }

      query = query.neq('status', 'cancelled');

      if (status !== 'all') {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;

      return ((data || []) as any[])
        .filter((row) => Boolean(row?.payment_id))
        .map((row) => ({
          ...row,
          contact_id: row.contact_id || '',
          cliente: row.cliente || 'Sem cliente',
          deal_id: row.deal_id || '',
          description: row.description || '',
          status: row.status || 'pending',
          amount: Number(row.amount || 0),
          paid_amount: Number(row.paid_amount || 0),
          valor_aberto: Number(row.valor_aberto || 0),
          dias_atraso: Number(row.dias_atraso || 0),
          collection_status: row.collection_status || null,
          vendedor: row.vendedor || '',
          business_unit: row.business_unit || 'all',
          brand: row.brand || 'all',
          currency: row.currency || 'USD',
        })) as ReceivablePayment[];
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

interface MarkAsPaidParams {
  paymentId: string;
  paidAmount: number;
  paidAt: string;
  paymentMethod?: string;
  bankAccountId?: string;
  notes?: string;
}

export function useMarkPaymentAsPaid() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ paymentId, paidAmount, paidAt, paymentMethod, bankAccountId, notes }: MarkAsPaidParams) => {
      try {
        const methodRaw = String(paymentMethod || '').toLowerCase();
        const methodForValidation = methodRaw || 'other';
        const isCashMethod = methodForValidation === 'cash';
        const requiresBankAccount = !isCashMethod;
        if (requiresBankAccount && !bankAccountId) {
          throw new Error('Conta bancária obrigatória para este método de recebimento');
        }

        // Get the current user's app_user_id
        const { data: { user } } = await supabase.auth.getUser();
        let appUserId: string | null = null;
        
        if (user) {
          const { data: appUser } = await supabase
            .from('app_users')
            .select('id')
            .eq('auth_user_id', user.id)
            .single();
          appUserId = appUser?.id ?? null;
        }
        
        const methodForTransaction = (() => {
          if (methodRaw === 'ach' || methodRaw === 'wire') return 'bank_transfer';
          return normalizePaymentMethod(methodRaw) || 'other';
        })();

        const { data, error } = await supabase
          .from('payments')
          .update({
            paid_amount: paidAmount,
            paid_at: paidAt,
            notes: notes,
            status: 'paid',
            paid_by: appUserId,
          })
          .eq('id', paymentId)
          .select()
          .single();
        
        if (error) throw error;

        // Keep operational trail: every manual receivable settlement writes one payment transaction.
        const dealId = data?.deal_id as string | undefined;
        if (!dealId) {
          throw new Error('Pagamento sem deal_id não pode gerar transação financeira');
        }

        let contactId: string | null = null;
        const { data: dealRow } = await supabase
          .from('deals')
          .select('contact_id')
          .eq('id', dealId)
          .maybeSingle();
        contactId = (dealRow?.contact_id as string | null) ?? null;

        const bankAccountAuditNote = bankAccountId ? `[bank_account_id:${bankAccountId}]` : '';
        const noteWithBankAccount = [notes?.trim(), bankAccountAuditNote].filter(Boolean).join('\n') || null;

        const { data: insertedTx, error: txError } = await supabase
          .from('payment_transactions')
          .insert({
            payment_id: paymentId,
            deal_id: dealId,
            contact_id: contactId,
            amount: paidAmount,
            currency: (data?.currency as string | null) || 'USD',
            payment_method: methodForTransaction,
            transaction_date: paidAt,
            transaction_type: 'manual_receipt',
            status: 'confirmed',
            recorded_by: appUserId,
            confirmed_by: appUserId,
            confirmed_at: new Date().toISOString(),
            notes: noteWithBankAccount,
            trigger_type: 'manual',
          } as any)
          .select('id')
          .single();

        if (txError) throw txError;

        // Every non-Stripe manual settlement should enter reconciliation queue.
        if (methodForTransaction !== 'stripe' && insertedTx?.id) {
          const { error: issueError } = await (supabase.from('finance_recon_issues' as any) as any)
            .insert({
              status: 'open',
              severity: 'medium',
              source: 'manual',
              issue_type: 'missing',
              payment_id: paymentId,
              payment_transaction_id: insertedTx.id,
              amount_expected: paidAmount,
              amount_found: null,
              currency: (data?.currency as string | null) || 'USD',
              owner_user_id: appUserId,
              resolution_note: null,
            } as any);
          if (issueError) throw issueError;
        }
        return data;
      } catch (error) {
        throw new Error(getFriendlyFinanceErrorMessage(error, 'Failed to mark payment as paid'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['payments-aging-detail'] });
      queryClient.invalidateQueries({ queryKey: ['payments-aging'] });
      queryClient.invalidateQueries({ queryKey: ['pdd-total'] });
      queryClient.invalidateQueries({ queryKey: ['payment-audit-report'] });
    },
  });
}

interface CancelPaymentParams {
  paymentId: string;
  cancellationReason: string;
}

export function useCancelPayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ paymentId, cancellationReason }: CancelPaymentParams) => {
      try {
        const { data, error } = await supabase
          .from('payments')
          .update({
            status: 'cancelled',
            cancelled_at: new Date().toISOString(),
            cancellation_reason: cancellationReason,
          })
          .eq('id', paymentId)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      } catch (error) {
        throw new Error(getFriendlyFinanceErrorMessage(error, 'Failed to cancel payment'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['payments-aging-detail'] });
      queryClient.invalidateQueries({ queryKey: ['payments-aging'] });
      queryClient.invalidateQueries({ queryKey: ['pdd-total'] });
      queryClient.invalidateQueries({ queryKey: ['payment-audit-report'] });
    },
  });
}

interface BulkUpdateParams {
  paymentIds: string[];
  updates: {
    status?: string;
    collection_status?: string;
    paid_at?: string;
    paid_by?: string | null;
  };
}

export function useBulkUpdatePayments() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ paymentIds, updates }: BulkUpdateParams) => {
      try {
        const { data, error } = await supabase
          .from('payments')
          .update(updates)
          .in('id', paymentIds)
          .select();
        
        if (error) throw error;
        return data;
      } catch (error) {
        throw new Error(getFriendlyFinanceErrorMessage(error, 'Failed to bulk update payments'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['payments-aging-detail'] });
      queryClient.invalidateQueries({ queryKey: ['payments-aging'] });
      queryClient.invalidateQueries({ queryKey: ['pdd-total'] });
      queryClient.invalidateQueries({ queryKey: ['payment-audit-report'] });
    },
  });
}
